<template>
  <div class="row h-100">
    <div class="col-12 h-100">
      <div class="poppins h-100 d-flex flex-column">
        <div class="d-flex justify-content-end mb-3 pb-1">
          <b-button v-if="isSaving" variant="primary" size="lg" class="pl-4 pr-4" disabled>
            <b-spinner small />
            {{ 'saving' | i18n }}
          </b-button>
          <b-button v-else variant="primary" size="lg" class="pl-4 pr-4" :disabled="disabledSave" @click="createCase">
            {{ 'save' | i18n }}
          </b-button>
        </div>
        <Loader v-if="!caseLoaded" />
        <div v-else class="overflow-x-hidden overflow-auto pr-1 flex1">
          <div v-if="isQr">
            <BlockHeader name="qr_code_expiry_time" class="mt-0" />
            <div class="row">
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Select v-model="caseData.expiresIn" :options="expiresInOptions" form-group-id="liveTime" :placeholder="'expiry_time' | i18n" />
              </div>
            </div>
          </div>
          <div>
            <BlockHeader name="Details" :class="`${isQr ? '' : 'mt-0'}`" />
            <div class="row">
              <div class="col-lg-8 col-sm-12 col-xs-12">
                <Input v-model="caseData.title" form-group-id="Title" placeholder="Title" :required="true" />
              </div>
              <div class="col-lg-4 col-sm-12 col-xs-12">
                <Select
                  v-model="caseData.templateId"
                  :options="templateOptions"
                  form-group-id="template"
                  placeholder="template"
                  :required="true"
                  :on-change="updateTemplate"
                />
              </div>
            </div>
            <div class="row justify-content-between">
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Select
                  v-model="caseData.team.id"
                  :on-change="
                    () => {
                      caseData.user.id = null;
                    }
                  "
                  :options="teamOptions"
                  form-group-id="Team"
                  placeholder="Team"
                  :required="true"
                />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Select
                  v-model="caseData.user.id"
                  :options="usersOptions"
                  form-group-id="User Responsible"
                  placeholder="User Responsible"
                  :required="true"
                  :disabled="!usersOptions.length"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Input v-model="caseData.caseNumberString" form-group-id="Case number" placeholder="case_number" />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Radio v-model="caseData.status" :options="statusOptions" placeholder="Status" :required="true" small form-group-id="Status" />
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Input
                  v-model="caseData.numberSortCriterium"
                  type="number"
                  form-group-id="Number Sort Criterium"
                  :placeholder="'numeric_sort_criterion' | i18n"
                />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Input
                  v-model="caseData.alphanumericSortCriterium"
                  form-group-id="Alphanumeric Sort Criterium"
                  :placeholder="'alphanumeric_sort_criterion' | i18n"
                />
              </div>
            </div>
          </div>
          <div>
            <BlockHeader name="Translation" />
            <div class="row">
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Select v-model="caseData.translateLanguage" :options="languageOptions" form-group-id="Language" placeholder="Language" />
              </div>
            </div>
          </div>
          <div>
            <BlockHeader name="Report Recipients" />
            <div class="row">
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Input v-model="caseData.email" form-group-id="Report Recipient" placeholder="Report Recipient" />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Input v-model="caseData.emailCC" :tile-size="14" form-group-id="Report Recipient CC" placeholder="Report Recipient CC" />
              </div>
            </div>
          </div>
          <div v-if="selectedTemplate">
            <BlockHeader name="Template Details" />
            <Group
              v-for="group in selectedTemplate.groups"
              :key="group.id"
              :group="group"
              :set-field-value="setFieldValue"
              :fields-value="fieldsValue"
              :lists="lists"
              :getFile="getFile"
              :getFileURL="getFileURL"
              groupName="Group"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

import Input from '../../common/input';
import Select from '../../common/select';
import Radio from '../../common/radio';
import BlockHeader from '../../common/block-header';
import Loader from '../../common/loader';

import Group from '../../reports/cases/components/group';

import { ReportsMixin, NotificationMixin, ApiMixin, ListMixin, AuthStorageMixin } from '../../../../mixins';
import { CASE_STATUSES, TRANSLATE_LANGUAGE, VALIDATION_RULES } from '../../../../../../../constants';
import api from '../../../api';

export default {
  name: 'NewCaseBase',
  components: { Input, Loader, Select, BlockHeader, Group, Radio },
  mixins: [ReportsMixin, NotificationMixin, ApiMixin, ListMixin, AuthStorageMixin],
  props: {
    onSubmit: {
      type: Function,
      default: () => {},
    },
    trigerPrevent: {
      type: Function,
      default: () => {},
    },
    isQr: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      loaded: false,
      lists: [],
      caseData: {
        user: {
          id: null,
        },
        team: {
          id: null,
        },
        status: CASE_STATUSES.ACTIVE,
      },
      fieldsValue: {},
      teams: [],
      templates: [],
      templateOptions: [],
      caseLoaded: false,
      statusOptions: Object.values(CASE_STATUSES).map((item) => ({
        value: item,
        text: this.$translate(item),
      })),
      languageOptions: Object.keys(TRANSLATE_LANGUAGE).map((i) => ({ value: TRANSLATE_LANGUAGE[i], text: this.$translate(i) })),
      expiresInOptions: [
        { value: 86400, text: `1 ${this.$translate('day')}` },
        { value: 86400 * 30, text: `30 ${this.$translate('days')}` },
        { value: 'never', text: this.$translate('never') },
      ],
      qrCodeTemplate: {},
      isSaving: false,
    };
  },
  computed: {
    usersOptions: function () {
      const teamID = this.caseData.team.id;
      const team = this.teams.find((i) => i.id === teamID) || [];
      const users = team.users;
      return users ? users.map((item) => ({ value: item.id, text: item.name })) : [];
    },
    teamOptions: function () {
      return this.teams.map((item) => ({ value: item.id, text: item.name }));
    },
    disabledSave: function () {
      const { templateId, title, team, user, status } = this.caseData;
      return !templateId || !title || !(team && team.id) || !(user && user.id) || !status;
    },
    selectedTemplate: function () {
      if (!this.caseData.templateId) {
        return null;
      }
      const template = this.templates.find((t) => t.id === this.caseData.templateId) || this.qrCodeTemplate;
      const groups = [];
      const groupsFields = {};

      template.fields.map((f) => {
        if (!groupsFields[f.groupId]) {
          groupsFields[f.groupId] = [];
        }
        f.required = f.rules.some((r) => r.name === VALIDATION_RULES.REQUIRED);
        groupsFields[f.groupId].push(f);
      });

      template.groups.map((g) => {
        const data = {
          name: g.name,
          id: g.id,
          fields: groupsFields[g.id],
        };
        groups.push(data);
      });
      return {
        name: template.name,
        id: template.id,
        groups,
      };
    },
  },
  watch: {
    caseData: {
      handler: function () {
        if (this.loaded) {
          this.trigerPrevent(false);
        }
      },
      deep: true,
    },
  },
  async created() {
    if (this.isQr) {
      this.caseData.expiresIn = 86400;
      if (this.$route.params.id) {
        const qrCode = await api.qrManagement.getById(this.$route.params.id);
        if (!qrCode) {
          return;
        }

        this.caseData = {
          expiresIn: qrCode.expiresIn,
          title: qrCode.title,
          caseNumberString: qrCode.caseNumberString,
          templateId: qrCode.template.id,
          team: { id: qrCode.team.id },
          user: { id: qrCode.user.id },
          status: qrCode.status,
          numberSortCriterium: qrCode.numberSortCriterium,
          alphanumericSortCriterium: qrCode.alphanumericSortCriterium,
          translateLanguage: qrCode.translateLanguage,
          email: qrCode.email,
          emailCC: qrCode.emailCC,
        };

        this.fieldsValue = {
          ...qrCode.report.fields,
          ...qrCode.fields,
        };

        this.qrCodeTemplate = qrCode.template;
      }
    }
    await this.loadTemplatesLookup();
    await this.loadTeamsLookup();
    const lists = await this.listService.get();
    this.lists = lists.data;

    setTimeout(() => {
      this.loaded = true;
    }, 100);
  },
  methods: {
    getFileURL(id) {
      const { access_token_link: token } = this.authStorageService.getAuth();
      const templateId = this.selectedTemplate.id;

      return `${axios.defaults.baseURL}/api/v1/reports/form-templates/${templateId}/files/${id}?authorization=${token}`;
    },
    async getFile(id) {
      try {
        await axios(this.getFileURL(id));
        window.open(this.getFileURL(id), '_blank');
      } catch (error) {
        this.showNotification({ type: 'error', message: this.$translate('file_do_not_uploaded') });
      }
    },
    updateTemplate() {
      const id = this.caseData.templateId;
      const temp = this.templates.find((t) => t.id === id);

      this.caseData.email = temp.email ? temp.email : '';
      this.caseData.emailCC = temp.emailCC ? temp.emailCC : '';
    },
    setFieldValue(field, value) {
      if (this.loaded) {
        this.trigerPrevent(false);
      }
      this.fieldsValue[field.id] = value;
    },
    async loadTeamsLookup() {
      const response = await this.reportsService.getTeams();
      this.teams = response;
      this.caseLoaded = true;
    },
    async loadTemplatesLookup() {
      const response = await this.reportsService.getTemplates();
      this.templates = response.data;
      this.templateOptions = response.data.map((item) => ({ value: item.id, text: item.name }));
    },
    async createCase() {
      this.isSaving = true;
      const {
        templateId,
        title,
        team,
        translateLanguage,
        caseNumberString,
        alphanumericSortCriterium,
        numberSortCriterium,
        email,
        emailCC,
        user,
        status,
        expiresIn,
      } = this.caseData;
      const fields = [];
      this.selectedTemplate.groups.forEach((g) => {
        if (g.fields) {
          g.fields.forEach((f) => {
            fields.push({
              ...f,
              value: this.fieldsValue[f.id],
              isAttachFileToEmail: Object.hasOwn(this.fieldsValue || {}, `${f.key}_attachToEmail`)
                ? this.fieldsValue[`${f.key}_attachToEmail`]
                : f.isAttachFileToEmail,
            });
          });
        }
      });
      const data = {
        userId: user.id,
        teamId: team.id,
        isQr: this.isQr,
        templateId,
        title,
        translateLanguage,
        caseNumberString,
        alphanumericSortCriterium,
        numberSortCriterium,
        email,
        emailCC,
        status,
        fields,
      };

      if (this.isQr) {
        if (this.$route.params.id) {
          data.id = this.$route.params.id;
        }
        data.expiresIn = expiresIn;
      }

      try {
        await this.onSubmit(data);
        this.showNotification({ type: 'success', message: this.$translate('success') });
        this.trigerPrevent(true);
        this.$router.push({ name: this.isQr ? 'qr-management' : 'cases' });
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.main-content {
  overflow-y: visible;
  max-height: 100%;
}
.inputs-block {
  padding-left: 20px;
  padding-right: 20px;
}
::v-deep .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
::v-deep .active-status {
  background-color: rgb(37, 154, 43);
}
::v-deep .inprogress-status {
  background-color: rgb(239, 255, 15);
}
::v-deep .finished-status {
  background-color: rgb(90, 98, 107);
}

.statuses-block {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  width: 100%;
}

.status-block {
  display: flex;
  margin-left: 20px;
  align-content: center;
  justify-content: center;
  align-items: center;
}

.status-name {
  margin-left: 10px;
}

::v-deep .details-action {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: rgb(34, 129, 254);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 28px;
  margin: auto;
}

::v-deep .download-buttons {
  display: flex;
}
::v-deep .download-button {
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  color: rgb(49, 141, 255);
  cursor: pointer;
}

::v-deep .zip-link {
  padding: 4.5px 13px;
  cursor: pointer;
  display: inline-block;
  outline: none;
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.5;

  text-decoration: none;
  margin-right: 25px;

  background: #edf8fe;
  border: 2px solid #afd8ff;
  border-radius: 4px;
  color: #318dff;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}

.overflow-x-hidden {
  overflow-x: hidden !important;
}
</style>
